import type { LocalLandingData } from "@/data/madrid-landings";

const SITE = "https://agenciaseoevolution.com";

/** Builds route head() metadata (meta, canonical, JSON-LD) for a local landing page. */
export function buildLocalLandingHead(data: LocalLandingData) {
  const canonicalUrl = `${SITE}/${data.slug}`;

  const businessSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${canonicalUrl}#business`,
    name: `SEOEVOLUTION — ${data.serviceName}`,
    url: canonicalUrl,
    description: data.serviceDescription,
    telephone: "+34910626684",
    email: "contacto@agenciaseoevolution.com",
    areaServed: { "@type": "City", name: "Madrid", address: { "@type": "PostalAddress", addressCountry: "ES" } },
    address: { "@type": "PostalAddress", addressLocality: "Madrid", addressRegion: "Comunidad de Madrid", addressCountry: "ES" },
    openingHours: ["Mo-Th 10:00-17:00", "Fr 10:00-14:00"],
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: data.serviceName,
    serviceType: data.serviceName,
    description: data.serviceDescription,
    provider: { "@type": "Organization", name: "SEOEVOLUTION", url: SITE },
    areaServed: { "@type": "City", name: "Madrid" },
    url: canonicalUrl,
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Inicio", item: SITE },
      { "@type": "ListItem", position: 2, name: "Servicios", item: `${SITE}/mas-servicios` },
      { "@type": "ListItem", position: 3, name: data.breadcrumbLabel, item: canonicalUrl },
    ],
  };

  return {
    meta: [
      { title: data.metaTitle },
      { name: "description", content: data.metaDescription },
      { name: "keywords", content: data.metaKeywords },
      { property: "og:type", content: "website" },
      { property: "og:url", content: canonicalUrl },
      { property: "og:title", content: data.metaTitle },
      { property: "og:description", content: data.ogDescription },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: data.metaTitle },
      { name: "twitter:description", content: data.ogDescription },
    ],
    links: [{ rel: "canonical", href: canonicalUrl }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(businessSchema) },
      { type: "application/ld+json", children: JSON.stringify(serviceSchema) },
      { type: "application/ld+json", children: JSON.stringify(faqSchema) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbSchema) },
    ],
  };
}
